import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import AutoBugReportDialog, { type AutoBugTrigger } from "./AutoBugReportDialog";

const COOLDOWN_MS = 5 * 60 * 1000; // one popup / 5 min per session
const DEBOUNCE_MS = 400;

/**
 * Listens for `auto-bug-report` events dispatched by debugCapture and the
 * ErrorBoundary, then opens the AutoBugReportDialog. Logged-in users only,
 * suppressed on admin and feedback routes, throttled to once per 5 minutes.
 */
export default function AutoBugReportProvider() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState<AutoBugTrigger | null>(null);
  const lastShownRef = useRef(0);
  const debounceRef = useRef<number | null>(null);
  const pendingRef = useRef<AutoBugTrigger | null>(null);

  const userRef = useRef(user);
  const pathRef = useRef(pathname);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { pathRef.current = pathname; }, [pathname]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<AutoBugTrigger>).detail;
      if (!detail) return;
      // Gate: signed-in users only, never on admin or feedback routes.
      if (!userRef.current) return;
      const p = pathRef.current || "";
      if (p.startsWith("/admin") || p.startsWith("/feedback")) return;
      // Throttle per session.
      if (Date.now() - lastShownRef.current < COOLDOWN_MS) return;
      // Debounce bursts — a single render error often fires many times.
      pendingRef.current = detail;
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        const t = pendingRef.current;
        if (!t) return;
        lastShownRef.current = Date.now();
        setTrigger(t);
        setOpen(true);
      }, DEBOUNCE_MS);
    };
    window.addEventListener("auto-bug-report", handler);
    return () => window.removeEventListener("auto-bug-report", handler);
  }, []);

  return (
    <AutoBugReportDialog
      open={open}
      trigger={trigger}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTrigger(null);
      }}
    />
  );
}